import { tr, useLanguage } from "../i18n";
import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { PageHero } from "./Shared";
import { Reveal, Kicker } from "../components/motion";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Free ERI", "Single-Entity AI Assessment", "Group-Wide AI Assessment", "Annual Recertification", "AI System Assessment", "AI Agent Assessment", "ESAIA Standards", "Other"];
const Field = ({
  label,
  required,
  children,
  htmlFor
}) => <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-medium text-esaia-ink">
      {label} {required && <span className="text-esaia-blue">*</span>}
    </label>
    {children}
  </div>;
const inputCls = "w-full border border-esaia-line bg-white px-3 py-2.5 text-sm text-esaia-ink placeholder:text-esaia-subtle focus:border-esaia-blue focus:outline-none";
export default function Contact() {
  const { lang } = useLanguage();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company: "",
    job_title: "",
    email: "",
    country: "",
    entities: "",
    ai_systems: "",
    ai_agents: "",
    service: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => {
    const s = params.get("service");
    if (s && SERVICES.includes(s)) setForm(f => ({
      ...f,
      service: s
    }));
  }, [params]);
  const set = k => e => setForm(f => ({
    ...f,
    [k]: e.target.value
  }));
  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      setSent(true);
      toast.success(tr("Your enquiry has been sent. ESAIA will respond shortly."));
    } catch {
      toast.error(tr("Something went wrong. Please try again or email info@esaia.org directly."));
    } finally {
      setLoading(false);
    }
  };
  return <div data-testid="contact-page">
      <PageHero kicker={tr("Contact ESAIA")} title={tr("Let's talk about your AI environment.")} copy={tr("Whether you are deploying your first AI system or managing AI across a complex international organization, ESAIA can help you determine the appropriate assessment scope.")} />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="border border-esaia-line bg-white p-8">
                <Kicker>{tr("Direct contact")}</Kicker>
                <div className="mt-6 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center border border-esaia-blue/25 text-esaia-blue">
                    <Mail size={18} strokeWidth={1.6} />
                  </span>
                  <div>
                    <p className="text-xs text-esaia-subtle">{tr("Email")}</p>
                    <a href="mailto:info@esaia.org" className="text-sm font-semibold text-esaia-blue hover:underline" data-testid="contact-email">{tr("info@esaia.org")}{" "}</a>
                  </div>
                </div>
                <p className="mt-6 text-sm leading-relaxed text-esaia-muted">{tr("For quotation requests, include the number of legal entities, AI systems and AI agents where known. We respond with a scope proposal and pricing indication.")}{" "}</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              {sent ? <div className="border border-esaia-blue/30 bg-white p-10 text-center" data-testid="contact-success">
                  <p className="font-serif text-2xl font-light text-esaia-ink">{tr("Thank you for your enquiry")}</p>
                  <p className="mt-3 text-sm text-esaia-muted">{tr("We have received your message and will contact you as soon as possible.")}</p>
                  <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-esaia-subtle">{tr("In the meantime, you can:")}</p>
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="contact-success-links">
                    <Link to={`/${lang}/eri`} className="group border border-esaia-line px-4 py-3 text-sm text-esaia-ink transition-colors hover:border-esaia-blue hover:text-esaia-blue" data-testid="success-link-eri">
                      {tr("Take the Free ERI")}
                    </Link>
                    <Link to={`/${lang}/insights`} className="group border border-esaia-line px-4 py-3 text-sm text-esaia-ink transition-colors hover:border-esaia-blue hover:text-esaia-blue" data-testid="success-link-insights">
                      {tr("View all insights")}
                    </Link>
                    <Link to={`/${lang}/services`} className="group border border-esaia-line px-4 py-3 text-sm text-esaia-ink transition-colors hover:border-esaia-blue hover:text-esaia-blue" data-testid="success-link-services">
                      {tr("Explore our services")}
                    </Link>
                  </div>
                </div> : <form onSubmit={submit} className="border border-esaia-line bg-white p-8 sm:p-10" data-testid="quote-form">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label={tr("First name")} required htmlFor="first_name">
                      <input id="first_name" required value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="field-first-name" />
                    </Field>
                    <Field label={tr("Last name")} required htmlFor="last_name">
                      <input id="last_name" required value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="field-last-name" />
                    </Field>
                    <Field label={tr("Company")} required htmlFor="company">
                      <input id="company" required value={form.company} onChange={set("company")} className={inputCls} data-testid="field-company" />
                    </Field>
                    <Field label={tr("Job title")} htmlFor="job_title">
                      <input id="job_title" value={form.job_title} onChange={set("job_title")} className={inputCls} data-testid="field-job-title" />
                    </Field>
                    <Field label={tr("Email")} required htmlFor="email">
                      <input id="email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="field-email" />
                    </Field>
                    <Field label={tr("Country")} htmlFor="country">
                      <input id="country" value={form.country} onChange={set("country")} className={inputCls} data-testid="field-country" />
                    </Field>
                    <Field label={tr("Number of entities")} htmlFor="entities">
                      <input id="entities" inputMode="numeric" value={form.entities} onChange={set("entities")} className={inputCls} placeholder={tr("e.g. 6")} data-testid="field-entities" />
                    </Field>
                    <Field label={tr("Approximate number of AI systems")} htmlFor="ai_systems">
                      <input id="ai_systems" inputMode="numeric" value={form.ai_systems} onChange={set("ai_systems")} className={inputCls} placeholder={tr("e.g. 12")} data-testid="field-ai-systems" />
                    </Field>
                    <Field label={tr("Approximate number of AI agents")} htmlFor="ai_agents">
                      <input id="ai_agents" inputMode="numeric" value={form.ai_agents} onChange={set("ai_agents")} className={inputCls} placeholder={tr("e.g. 3")} data-testid="field-ai-agents" />
                    </Field>
                    <Field label={tr("Service interested in")} htmlFor="service">
                      <select id="service" value={form.service} onChange={set("service")} className={inputCls} data-testid="field-service">
                        <option value="">{tr("Select a service")}</option>
                        {SERVICES.map(s => <option key={s} value={s}>{tr(s)}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="mt-5">
                    <Field label={tr("Message")} htmlFor="message">
                      <textarea id="message" rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder={tr("Tell us about your AI environment and what you would like to assess.")} data-testid="field-message" />
                    </Field>
                  </div>
                  <button type="submit" disabled={loading} data-testid="quote-form-submit" className="mt-7 w-full bg-esaia-blue px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark disabled:opacity-60 sm:w-auto">
                    {loading ? tr("Sending...") : tr("Request a Consultation")}
                  </button>
                </form>}
            </Reveal>
          </div>
        </div>
      </section>
    </div>;
}
